import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Card, Table, Row, Col, Form, InputGroup, Button, Dropdown, Modal, Alert, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import { Link } from "react-router-dom";
import { fetchSMSList } from "../services/api";
import PaginationControl from "../components/PaginationControl";

// Données de démonstration en cas d'échec API
const demoMessages = [];

// ✅ Configuration des statuts
const STATUS_CONFIG = {
  SENT:    { label: 'Envoyé',      color: '#10b981', bg: '#d1fae5', icon: 'ri-check-double-line',   variant: 'success' },
  FAILED:  { label: 'Échec',       color: '#ef4444', bg: '#fee2e2', icon: 'ri-close-circle-line',   variant: 'danger'  },
  PENDING: { label: 'En attente',  color: '#f59e0b', bg: '#fef3c7', icon: 'ri-time-line',           variant: 'warning' },
  SENDING: { label: 'En cours',    color: '#3b82f6', bg: '#eff6ff', icon: 'ri-loader-4-line',       variant: 'primary' },
  UNKNOWN: { label: 'Inconnu',     color: '#6b7280', bg: '#f3f4f6', icon: 'ri-question-line',       variant: 'secondary' },
};

export default function MessageHistory() {
  const [messages, setMessages] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('send_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Ref pour éviter le double fetch
  const initDoneRef = useRef(false);

  // ============================================
  // UTILITAIRES
  // ============================================
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleString('fr-FR');
    try {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR');
    } catch {
      return dateString;
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = Math.floor((now - date) / 1000);
      if (diff < 60) return 'à l\'instant';
      if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
      if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
      if (diff < 604800) return `il y a ${Math.floor(diff / 86400)}j`;
      return date.toLocaleDateString('fr-FR');
    } catch {
      return '';
    }
  };

  const mapStatus = (statusCode, responseMessage) => {
    if (statusCode === '200' || statusCode === 'SENT' || responseMessage === 'Success') return 'SENT';
    if (statusCode === '400' || statusCode === 'FAILED' || responseMessage === 'Failed') return 'FAILED';
    if (statusCode === '500') return 'FAILED';
    if (statusCode === '201') return 'SENDING';
    if (statusCode === '202') return 'PENDING';
    return 'UNKNOWN';
  };

  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.UNKNOWN;

  const mapType = (type) => {
    const typeMap = {
      '1': 'SMS Pro',
      '2': 'SMS Standard',
      '3': 'SMS Flash'
    };
    return typeMap[type] || 'SMS Standard';
  };

  const calculateCost = (price, nbSms) => {
    const cost = (price || 0) * (nbSms || 1);
    return cost.toFixed(2);
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 'N/A';
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const duration = (end - start) / 1000;
      return `${duration}s`;
    } catch {
      return 'N/A';
    }
  };

  const detectOperator = (msisdn) => {
    if (!msisdn) return 'Inconnu';
    const operators = {
      '24177': 'Airtel',
      '24106': 'Moov',
      '24105': 'Moov',
      '24104': 'Azur',
      '24101': 'Libertis'
    };
    const prefix = msisdn.substring(0, 5);
    return operators[prefix] || 'Inconnu';
  };

  const mapPriority = (type) => {
    const priorityMap = { '1': 'Haute', '2': 'Normale', '3': 'Basse' };
    return priorityMap[type] || 'Normale';
  };

  // ============================================
  // FORMATAGE API
  // ============================================
  const formatMessageFromAPI = useCallback((message) => {
    const status = mapStatus(message.sms_status, message.response_message);
    const statusConfig = getStatusConfig(status);

    return {
      id: message.id,
      texte: message.message || '',
      destinataires: message.msisdn || '',
      type: mapType(message.type),
      dateEnvoi: formatDate(message.send_date),
      dateEnvoiRaw: message.send_date,
      timeAgo: formatTimeAgo(message.send_date),
      statut: status,
      statutLabel: statusConfig.label,
      statutVariant: statusConfig.variant,
      statutColor: statusConfig.color,
      statutBg: statusConfig.bg,
      statutIcon: statusConfig.icon,
      cout: calculateCost(message.sms_price, message.nb_sms),
      reference: message.sms_id || `REF-${message.id}`,
      expediteur: message.sender || 'N/A',
      dateReponse: formatDate(message.result_date),
      business: message.business_name || 'N/A',
      details: {
        operateur: detectOperator(message.msisdn),
        duree: calculateDuration(message.send_date, message.result_date),
        taille: `${message.message?.length || 0} caractères`,
        priorite: mapPriority(message.type),
        campagne: message.business_name || 'N/A',
        statutTechnique: message.response_message || 'N/A',
        route: message.sms_route || '1000',
        nbSms: message.nb_sms || 1
      }
    };
  }, []);

  // ============================================
  // CHARGEMENT API
  // ============================================
  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;

    const loadMessages = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('authToken');
        if (!token) throw new Error('Token d\'authentification manquant. Veuillez vous reconnecter.');

        const data = await fetchSMSList(token);
        const formattedMessages = data.map((message) => formatMessageFromAPI(message));
        setMessages(formattedMessages);
      } catch (err) {
        console.error('Erreur chargement messages:', err);
        setError(err.message);
        const demoFormatted = demoMessages.map(msg => formatMessageFromAPI(msg));
        setMessages(demoFormatted);
      } finally {
        setLoading(false);
      }
    };

    loadMessages();
  }, [formatMessageFromAPI]);

  const handleReload = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant.');

      const data = await fetchSMSList(token);
      const formattedMessages = data.map(msg => formatMessageFromAPI(msg));
      setMessages(formattedMessages);
    } catch (err) {
      console.error('Erreur rechargement messages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FILTRAGE & TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    let filtered = messages.filter(item => {
      const matchText = !filterText ||
        item.texte.toLowerCase().includes(filterText.toLowerCase()) ||
        item.destinataires.includes(filterText) ||
        item.type.toLowerCase().includes(filterText.toLowerCase()) ||
        item.statutLabel.toLowerCase().includes(filterText.toLowerCase()) ||
        item.expediteur.toLowerCase().includes(filterText.toLowerCase());

      const matchStatus = filterStatus === 'all' || item.statut === filterStatus;

      return matchText && matchStatus;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [filterText, filterStatus, sortField, sortDirection, messages]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedMessages(currentItems.map(msg => msg.id));
    } else {
      setSelectedMessages([]);
    }
  };

  const handleSelectMessage = (messageId) => {
    setSelectedMessages(prev =>
      prev.includes(messageId)
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  const handleShowDetails = (message) => {
    setSelectedMessage(message);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMessage(null);
  };

  const isAllSelected = currentItems.length > 0 && selectedMessages.length === currentItems.length;
  const isSomeSelected = selectedMessages.length > 0 && selectedMessages.length < currentItems.length;

  // ============================================
  // STATISTIQUES
  // ============================================
  const stats = useMemo(() => {
    const total = messages.length;
    const sent = messages.filter(m => m.statut === 'SENT').length;
    const failed = messages.filter(m => m.statut === 'FAILED').length;
    const pending = messages.filter(m => m.statut === 'PENDING' || m.statut === 'SENDING').length;
    const totalCost = messages.reduce((sum, m) => sum + parseFloat(m.cout || 0), 0);
    return { total, sent, failed, pending, totalCost };
  }, [messages]);

  // ============================================
  // EXPORT
  // ============================================
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedMessages.length > 0
      ? messages.filter(msg => selectedMessages.includes(msg.id))
      : filteredAndSortedItems;

    if (!dataToExport.length) {
      alert("Aucune donnée à exporter");
      setExportLoading(false);
      return;
    }

    if (format === "csv") {
      const headers = ["Texte", "Destinataires", "Type", "Date Envoi", "Statut", "Référence", "Expéditeur"];
      const csvContent = [
        "\uFEFF" + headers.join(";"),
        ...dataToExport.map(m => [
          m.texte, m.destinataires, m.type, m.dateEnvoi, m.statutLabel, m.reference, m.expediteur
        ].map(v => `"${v}"`).join(";"))
      ].join("\n");
      downloadFile(csvContent, "text/csv;charset=utf-8;", `export-sms-${Date.now()}.csv`);

    } else if (format === "excel") {
      const ws = XLSX.utils.json_to_sheet(dataToExport.map(m => ({
        Texte: m.texte,
        Destinataires: m.destinataires,
        Type: m.type,
        "Date Envoi": m.dateEnvoi,
        Statut: m.statutLabel,
        Coût: m.cout,
        Référence: m.reference,
        Expéditeur: m.expediteur
      })));
      ws['!cols'] = [{ wch: 40 }, { wch: 15 }, { wch: 12 }, { wch: 18 }, { wch: 15 }, { wch: 8 }, { wch: 12 }, { wch: 12 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Historique SMS");
      XLSX.writeFile(wb, `export-sms-${Date.now()}.xlsx`);

    } else if (format === "json") {
      downloadFile(JSON.stringify(dataToExport, null, 2), "application/json;charset=utf-8;", `export-sms-${Date.now()}.json`);

    } else if (format === "pdf") {
      generatePDF(dataToExport);
    }

    setExportLoading(false);
  };

  const generatePDF = (data) => {
    const doc = new jsPDF();
    const primaryColor = [41, 128, 185];
    const headerColor = [240, 240, 240];
    const alternateColor = [248, 249, 250];

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("HISTORIQUE DES MESSAGES SMS", 105, 15, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR')}`, 15, 22);
    doc.text(`Total: ${data.length} message(s)`, 180, 22, { align: 'right' });

    const pageWidth = 210;
    const margin = 15;
    let yPosition = 35;
    const colWidths = [40, 25, 20, 25, 20, 25, 25];
    const headers = ["MESSAGE", "DESTINATAIRE", "TYPE", "DATE ENVOI", "STATUT", "RÉFÉRENCE", "EXPÉDITEUR"];

    doc.setFillColor(...headerColor);
    doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    let xPosition = margin;
    headers.forEach((header, index) => {
      doc.text(header, xPosition + 2, yPosition);
      xPosition += colWidths[index];
    });
    yPosition += 6;
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");

    data.forEach((item, index) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
        doc.setFillColor(...headerColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        let newX = margin;
        headers.forEach((header, idx) => {
          doc.text(header, newX + 2, yPosition);
          newX += colWidths[idx];
        });
        yPosition += 6;
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
      }

      if (index % 2 === 0) {
        doc.setFillColor(...alternateColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 6, 'F');
      }

      doc.setTextColor(0, 0, 0);
      const rowData = [
        item.texte.length > 25 ? item.texte.substring(0, 25) + '...' : item.texte,
        item.destinataires,
        item.type,
        item.dateEnvoi,
        item.statutLabel,
        item.reference.length > 15 ? item.reference.substring(0, 15) + '...' : item.reference,
        item.expediteur
      ];

      let currentX = margin;
      rowData.forEach((cell, cellIndex) => {
        if (cellIndex === 4) {
          if (item.statut === "SENT") doc.setTextColor(39, 174, 96);
          else if (item.statut === "FAILED") doc.setTextColor(231, 76, 60);
          else if (item.statut === "PENDING") doc.setTextColor(243, 156, 18);
          else doc.setTextColor(52, 152, 219);
        } else if (cellIndex === 5) {
          doc.setTextColor(231, 76, 60);
        } else {
          doc.setTextColor(0, 0, 0);
        }
        doc.text(cell, currentX + 1, yPosition);
        currentX += colWidths[cellIndex];
      });
      yPosition += 6;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 275, pageWidth - margin, 275);
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.text(`Page ${i} sur ${pageCount}`, 105, 282, { align: 'center' });
    }

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    doc.save(`historique-sms-${timestamp}.pdf`);
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  const handleDownloadDetails = (message, format = 'pdf') => {
    if (!message) return;
    if (format === 'pdf') downloadMessageDetailsPDF(message);
    else if (format === 'text') downloadMessageDetailsText(message);
    else if (format === 'json') downloadMessageDetailsJSON(message);
  };

  const downloadMessageDetailsPDF = (message) => {
    const doc = new jsPDF();
    const primaryColor = [41, 128, 185];
    const secondaryColor = [240, 240, 240];

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("DÉTAIL DU MESSAGE SMS", 105, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Référence: ${message.reference}`, 15, 22);
    doc.text(`ID: ${message.id}`, 180, 22, { align: 'right' });

    let yPosition = 35;
    doc.setFillColor(...secondaryColor);
    doc.rect(15, yPosition, 180, 8, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("INFORMATIONS PRINCIPALES", 20, yPosition + 5);

    yPosition += 15;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Contenu:", 20, yPosition);
    doc.setFont("helvetica", "normal");
    const splitText = doc.splitTextToSize(message.texte, 170);
    doc.text(splitText, 20, yPosition + 5);
    yPosition += (splitText.length * 5) + 10;

    const infoData = [
      ["Destinataire:", message.destinataires],
      ["Expéditeur:", message.expediteur],
      ["Type:", message.type],
      ["Date d'envoi:", message.dateEnvoi],
      ["Statut:", message.statutLabel],
      ["Référence:", message.reference],
    ];

    infoData.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, 20, yPosition);
      doc.setFont("helvetica", "normal");
      doc.text(value, 60, yPosition);
      yPosition += 6;
    });

    doc.save(`details-message-${message.reference}-${message.id}.pdf`);
  };

  const downloadMessageDetailsText = (message) => {
    const content = `DÉTAIL DU MESSAGE SMS\n====================\n\nMessage: ${message.texte}\nDestinataire: ${message.destinataires}\nExpéditeur: ${message.expediteur}\nType: ${message.type}\nDate: ${message.dateEnvoi}\nStatut: ${message.statutLabel}\nRéférence: ${message.reference}`;
    downloadFile(content, "text/plain;charset=utf-8;", `details-message-${message.reference}-${message.id}.txt`);
  };

  const downloadMessageDetailsJSON = (message) => {
    downloadFile(JSON.stringify(message, null, 2), "application/json;charset=utf-8;", `details-message-${message.reference}-${message.id}.json`);
  };

  // ============================================
  // SUB-COMPONENTS
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th
      className="logs-sortable-header"
      onClick={() => handleSort(field)}
    >
      <span className="logs-sortable-content">
        {children}
        {sortField === field && (
          <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-line logs-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  return (
    <div className="msg-history main main-app p-3 p-lg-4">
      {/* ========== HEADER ========== */}
      <div className="msg-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="msg-header-icon">
              <i className="ri-history-line"></i>
            </div>
            <div>
              <h3 className="msg-header-title mb-1">Historique des messages</h3>
              <p className="msg-header-subtitle mb-0">
                Consultez et gérez tous vos SMS envoyés
              </p>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button className="msg-btn-secondary" onClick={handleReload} disabled={loading}>
              <i className={`ri-refresh-line ${loading ? 'spinning' : ''}`}></i>
              <span>Actualiser</span>
            </Button>

            <Dropdown>
              <Dropdown.Toggle className="msg-btn-secondary" disabled={exportLoading}>
                <i className={`ri-download-2-line ${exportLoading ? 'spinning' : ''}`}></i>
                <span>Exporter</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="msg-dropdown-menu">
                <Dropdown.Item onClick={() => handleExport('csv')}>
                  <i className="ri-file-text-line text-primary me-2"></i>
                  Export CSV
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('excel')}>
                  <i className="ri-file-excel-line text-success me-2"></i>
                  Export Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('pdf')}>
                  <i className="ri-file-pdf-line text-danger me-2"></i>
                  Export PDF
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('json')}>
                  <i className="ri-code-line text-warning me-2"></i>
                  Export JSON
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Button as={Link} to="/apps/Envoie" className="msg-btn-primary">
              <i className="ri-add-line"></i>
              <span>Nouveau SMS</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={3}>
          <div className="msg-stat-card msg-stat-blue">
            <div className="msg-stat-icon-wrapper msg-stat-icon-blue">
              <i className="ri-mail-send-line"></i>
            </div>
            <div className="msg-stat-content">
              <div className="msg-stat-label">Total</div>
              <div className="msg-stat-value">{stats.total.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="msg-stat-card msg-stat-green">
            <div className="msg-stat-icon-wrapper msg-stat-icon-green">
              <i className="ri-check-double-line"></i>
            </div>
            <div className="msg-stat-content">
              <div className="msg-stat-label">Envoyés</div>
              <div className="msg-stat-value">{stats.sent.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="msg-stat-card msg-stat-red">
            <div className="msg-stat-icon-wrapper msg-stat-icon-red">
              <i className="ri-close-circle-line"></i>
            </div>
            <div className="msg-stat-content">
              <div className="msg-stat-label">Échecs</div>
              <div className="msg-stat-value">{stats.failed.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={3}>
          <div className="msg-stat-card msg-stat-amber">
            <div className="msg-stat-icon-wrapper msg-stat-icon-amber">
              <i className="ri-time-line"></i>
            </div>
            <div className="msg-stat-content">
              <div className="msg-stat-label">En attente</div>
              <div className="msg-stat-value">{stats.pending.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="msg-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={6} md={12}>
            <div className="msg-search-wrapper">
              <i className="ri-search-line msg-search-icon"></i>
              <input
                type="text"
                className="msg-search-input"
                placeholder="Rechercher un message, destinataire, expéditeur..."
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
              />
              {filterText && (
                <button className="msg-search-clear" onClick={() => setFilterText('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="msg-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="SENT">Envoyés</option>
              <option value="FAILED">Échecs</option>
              <option value="PENDING">En attente</option>
              <option value="SENDING">En cours</option>
            </select>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="msg-select"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              <option value={5}>5 par page</option>
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
            </select>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="msg-table-card">
        {loading ? (
          <div className="msg-loading">
            <div className="msg-loading-spinner"></div>
            <div className="msg-loading-text">Chargement des messages...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="msg-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="msg-checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="msg-checkbox"
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <SortableHeader field="texte">Message</SortableHeader>
                    <SortableHeader field="destinataires">Destinataire</SortableHeader>
                    <SortableHeader field="expediteur">Expéditeur</SortableHeader>
                    <SortableHeader field="type">Type</SortableHeader>
                    <SortableHeader field="dateEnvoiRaw">Date</SortableHeader>
                    <SortableHeader field="statutLabel">Statut</SortableHeader>
                    <th style={{ width: '80px' }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((msg) => {
                      const statusConfig = getStatusConfig(msg.statut);
                      return (
                        <tr key={msg.id} className="msg-row">
                          <td>
                            <div className="msg-checkbox-wrapper">
                              <input
                                type="checkbox"
                                className="msg-checkbox"
                                checked={selectedMessages.includes(msg.id)}
                                onChange={() => handleSelectMessage(msg.id)}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="msg-message-cell">
                              <div className="msg-message-text" title={msg.texte}>
                                {msg.texte.length > 60 ? msg.texte.substring(0, 60) + '…' : msg.texte}
                              </div>
                              <div className="msg-message-ref">
                                <i className="ri-file-copy-line"></i>
                                {msg.reference}
                              </div>
                            </div>
                          </td>
                          <td>
                            <code className="msg-code">{msg.destinataires}</code>
                          </td>
                          <td>
                            <span className="msg-badge msg-badge-info">
                              <i className="ri-user-line"></i>
                              {msg.expediteur}
                            </span>
                          </td>
                          <td>
                            <span className="msg-badge msg-badge-light">
                              {msg.type}
                            </span>
                          </td>
                          <td>
                            <div className="msg-date-cell">
                              <div className="msg-date-main">{msg.dateEnvoi.split(' ')[0]}</div>
                              <div className="msg-date-time">{msg.dateEnvoi.split(' ')[1] || ''}</div>
                              <div className="msg-date-ago">{msg.timeAgo}</div>
                            </div>
                          </td>
                          <td>
                            <span
                              className="msg-status-badge"
                              style={{
                                color: statusConfig.color,
                                backgroundColor: statusConfig.bg,
                              }}
                            >
                              <i className={statusConfig.icon}></i>
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="text-end">
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Voir les détails</Tooltip>}
                            >
                              <button
                                className="msg-action-btn"
                                onClick={() => handleShowDetails(msg)}
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                            </OverlayTrigger>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="msg-empty-state">
                          <div className="msg-empty-icon">
                            <i className="ri-inbox-line"></i>
                          </div>
                          <h5 className="msg-empty-title">Aucun message trouvé</h5>
                          <p className="msg-empty-text">
                            {filterText || filterStatus !== 'all'
                              ? "Aucun message ne correspond à vos critères"
                              : "Aucun message n'a été envoyé pour le moment"}
                          </p>
                          {(filterText || filterStatus !== 'all') && (
                            <Button
                              className="msg-btn-empty-reset"
                              onClick={() => { setFilterText(''); setFilterStatus('all'); }}
                            >
                              <i className="ri-refresh-line me-2"></i>
                              Réinitialiser les filtres
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* ========== PAGINATION ========== */}
            {filteredAndSortedItems.length > 0 && (
              <div className="msg-pagination">
                <div className="msg-pagination-info">
                  <i className="ri-information-line"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                  {selectedMessages.length > 0 && (
                    <span className="msg-pagination-selected">
                      • <strong>{selectedMessages.length}</strong> sélectionné{selectedMessages.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {totalPages > 1 && (
                  <PaginationControl
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    maxVisiblePages={5}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ========== MODAL DÉTAILS ========== */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        className="msg-modal"
      >
        <div className="msg-modal-header">
          <div className="d-flex align-items-center gap-3">
            <div className="msg-modal-header-icon">
              <i className="ri-message-3-line"></i>
            </div>
            <div>
              <h5 className="msg-modal-title mb-0">Détails du message</h5>
              <small className="msg-modal-subtitle">Minerve Tech • Analyse complète</small>
            </div>
          </div>
          <button className="msg-modal-close" onClick={handleCloseModal}>
            <i className="ri-close-line"></i>
          </button>
        </div>

        <Modal.Body className="msg-modal-body">
          {selectedMessage && (
            <div className="msg-details">
              {/* Statut */}
              <div className="msg-details-status">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="msg-details-status-icon"
                    style={{
                      color: selectedMessage.statutColor,
                      backgroundColor: selectedMessage.statutBg,
                    }}
                  >
                    <i className={selectedMessage.statutIcon}></i>
                  </div>
                  <div>
                    <h5 className="msg-details-title mb-1">
                      Message #{selectedMessage.id}
                    </h5>
                    <p className="msg-details-subtitle mb-0">
                      {selectedMessage.dateEnvoi} • {selectedMessage.timeAgo}
                    </p>
                  </div>
                </div>
                <span
                  className="msg-status-badge"
                  style={{
                    color: selectedMessage.statutColor,
                    backgroundColor: selectedMessage.statutBg,
                  }}
                >
                  <i className={selectedMessage.statutIcon}></i>
                  {selectedMessage.statutLabel}
                </span>
              </div>

              {/* Contenu */}
              <div className="msg-details-section">
                <div className="msg-details-section-header">
                  <i className="ri-chat-quote-line"></i>
                  <span>Contenu du message</span>
                </div>
                <div className="msg-details-content">
                  {selectedMessage.texte}
                </div>
                <div className="msg-details-content-meta">
                  <span><i className="ri-text"></i> {selectedMessage.texte.length} caractères</span>
                  <span><i className="ri-stack-line"></i> {selectedMessage.details?.nbSms || 1} SMS</span>
                </div>
              </div>

              {/* Infos principales */}
              <Row className="g-3">
                <Col md={6}>
                  <div className="msg-details-info">
                    <div className="msg-details-info-icon msg-details-info-icon-green">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <div className="msg-details-info-label">Destinataire</div>
                      <div className="msg-details-info-value">{selectedMessage.destinataires}</div>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="msg-details-info">
                    <div className="msg-details-info-icon msg-details-info-icon-blue">
                      <i className="ri-user-line"></i>
                    </div>
                    <div>
                      <div className="msg-details-info-label">Expéditeur</div>
                      <div className="msg-details-info-value">{selectedMessage.expediteur}</div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Détails techniques */}
              {selectedMessage.details && (
                <div className="msg-details-section">
                  <div className="msg-details-section-header">
                    <i className="ri-settings-3-line"></i>
                    <span>Informations techniques</span>
                  </div>
                  <Row className="g-2">
                    {[
                      { icon: 'ri-building-line', label: 'Opérateur', value: selectedMessage.details.operateur },
                      { icon: 'ri-timer-line', label: 'Durée', value: selectedMessage.details.duree },
                      { icon: 'ri-ruler-line', label: 'Taille', value: selectedMessage.details.taille },
                      { icon: 'ri-flag-line', label: 'Priorité', value: selectedMessage.details.priorite },
                      { icon: 'ri-shield-check-line', label: 'Statut technique', value: selectedMessage.details.statutTechnique },
                      { icon: 'ri-route-line', label: 'Route SMS', value: selectedMessage.details.route }
                    ].map((item, index) => (
                      <Col md={6} key={index}>
                        <div className="msg-details-tech-item">
                          <div className="d-flex align-items-center gap-2">
                            <i className={`${item.icon} msg-details-tech-icon`}></i>
                            <span className="msg-details-tech-label">{item.label}</span>
                          </div>
                          <span className="msg-details-tech-value">{item.value}</span>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <div className="msg-modal-footer">
          <Button className="msg-btn-cancel" onClick={handleCloseModal}>
            Fermer
          </Button>
          <Dropdown>
            <Dropdown.Toggle className="msg-btn-primary">
              <i className="ri-download-2-line me-2"></i>
              Exporter
            </Dropdown.Toggle>
            <Dropdown.Menu className="msg-dropdown-menu" align="end">
              <Dropdown.Item onClick={() => handleDownloadDetails(selectedMessage, 'pdf')}>
                <i className="ri-file-pdf-line text-danger me-2"></i>
                PDF
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleDownloadDetails(selectedMessage, 'text')}>
                <i className="ri-file-text-line text-dark me-2"></i>
                Texte
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleDownloadDetails(selectedMessage, 'json')}>
                <i className="ri-code-line text-warning me-2"></i>
                JSON
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Modal>

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .msg-history {
          background: #f8fafc;
          min-height: 100vh;
        }
        .msg-history * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .msg-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .msg-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .msg-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .msg-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* ========== BUTTONS ========== */
        .msg-btn-primary, .msg-btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
        }
        .msg-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .msg-btn-primary:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .msg-btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .msg-btn-secondary:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #0f172a;
          transform: translateY(-1px);
        }
        .msg-btn-secondary:disabled {
          opacity: 0.6;
        }
        .msg-dropdown-menu {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.1);
          padding: 6px;
        }
        .msg-dropdown-menu .dropdown-item {
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
        }
        .msg-dropdown-menu .dropdown-item:hover {
          background: #f1f5f9;
        }

        /* ========== STATS ========== */
        .msg-stat-card {
          display: flex;
          align-items: center;
          gap: 14px;
          background: white;
          border-radius: 14px;
          padding: 18px 20px;
          border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .msg-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .msg-stat-icon-wrapper {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .msg-stat-icon-blue   { background: #eff6ff; color: #3b82f6; }
        .msg-stat-icon-green  { background: #d1fae5; color: #10b981; }
        .msg-stat-icon-red    { background: #fee2e2; color: #ef4444; }
        .msg-stat-icon-amber  { background: #fef3c7; color: #f59e0b; }
        .msg-stat-content {
          min-width: 0;
          flex-grow: 1;
        }
        .msg-stat-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }
        .msg-stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
          line-height: 1;
        }

        /* ========== FILTRES ========== */
        .msg-filters {
          background: white;
          border-radius: 14px;
          padding: 16px 20px;
          border: 1px solid #e2e8f0;
        }
        .msg-search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .msg-search-icon {
          position: absolute;
          left: 14px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
        }
        .msg-search-input {
          width: 100%;
          padding: 11px 40px 11px 42px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          background: #f8fafc;
          transition: all 0.2s;
          outline: none;
        }
        .msg-search-input:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .msg-search-clear {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1.1rem;
          padding: 6px 10px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s;
        }
        .msg-search-clear:hover {
          background: #fee2e2;
          color: #ef4444;
        }
        .msg-select {
          width: 100%;
          padding: 11px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
          background: #f8fafc;
          color: #334155;
          outline: none;
          transition: all 0.2s;
          cursor: pointer;
        }
        .msg-select:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        /* ========== TABLE ========== */
        .msg-table-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .msg-table {
          font-size: 0.875rem;
        }
        .msg-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 16px;
          border-bottom: 1px solid #e2e8f0;
          border-top: none;
          white-space: nowrap;
        }
        .msg-table tbody td {
          padding: 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .msg-row {
          transition: background-color 0.15s;
        }
        .msg-row:hover {
          background-color: #f8fafc;
        }
        .msg-sortable-header {
          cursor: pointer;
          user-select: none;
        }
        .msg-sortable-header:hover {
          color: #3b82f6;
        }
        .msg-sortable-content {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .msg-sort-icon {
          color: #3b82f6;
          font-size: 0.9rem;
        }

        /* Checkbox */
        .msg-checkbox-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .msg-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1.5px solid #cbd5e1;
          cursor: pointer;
          accent-color: #3b82f6;
        }

        /* Message cell */
        .msg-message-cell {
          max-width: 320px;
        }
        .msg-message-text {
          font-weight: 500;
          color: #0f172a;
          font-size: 0.85rem;
          line-height: 1.4;
          margin-bottom: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        .msg-message-ref {
          font-size: 0.7rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 4px;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }

        /* Code */
        .msg-code {
          font-size: 0.78rem;
          color: #1e40af;
          background: #eff6ff;
          padding: 4px 8px;
          border-radius: 5px;
          font-weight: 600;
        }

        /* Badges */
        .msg-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.72rem;
          font-weight: 600;
          white-space: nowrap;
        }
        .msg-badge-info {
          background: #cffafe;
          color: #0891b2;
        }
        .msg-badge-light {
          background: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
        }

        /* Date cell */
        .msg-date-cell {
          line-height: 1.3;
        }
        .msg-date-main {
          font-weight: 600;
          color: #0f172a;
          font-size: 0.8rem;
          font-variant-numeric: tabular-nums;
        }
        .msg-date-time {
          font-size: 0.72rem;
          color: #64748b;
          font-variant-numeric: tabular-nums;
        }
        .msg-date-ago {
          font-size: 0.68rem;
          color: #94a3b8;
          margin-top: 2px;
        }

        /* Status badge */
        .msg-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 11px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 0.72rem;
          white-space: nowrap;
        }

        /* Action button */
        .msg-action-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.9rem;
        }
        .msg-action-btn:hover {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .msg-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .msg-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .msg-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .msg-empty-state {
          padding: 60px 20px;
          text-align: center;
        }
        .msg-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
        }
        .msg-empty-title {
          color: #334155;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .msg-empty-text {
          color: #94a3b8;
          margin-bottom: 20px;
        }
        .msg-btn-empty-reset {
          background: white;
          color: #3b82f6;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          font-weight: 600;
          padding: 8px 16px;
        }
        .msg-btn-empty-reset:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== PAGINATION ========== */
        .msg-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }
        .msg-pagination-info {
          font-size: 0.8rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .msg-pagination-info strong {
          color: #0f172a;
        }
        .msg-pagination-selected {
          color: #3b82f6;
          margin-left: 8px;
        }

        /* ========== MODAL ========== */
        .msg-modal .modal-content {
          border: none;
          border-radius: 16px;
          overflow: hidden;
        }
        .msg-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
        }
        .msg-modal-header-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .msg-modal-title {
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .msg-modal-subtitle {
          color: #94a3b8;
          font-size: 0.8rem;
        }
        .msg-modal-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1.1rem;
        }
        .msg-modal-close:hover {
          background: rgba(255,255,255,0.2);
          transform: rotate(90deg);
        }
        .msg-modal-body {
          padding: 24px;
          background: #f8fafc;
          max-height: 60vh;
          overflow-y: auto;
        }
        .msg-modal-footer {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }
        .msg-btn-cancel {
          background: white;
          color: #334155;
          border: 1px solid #e2e8f0;
          padding: 10px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
        }
        .msg-btn-cancel:hover {
          background: #f1f5f9;
          color: #0f172a;
        }

        /* Details */
        .msg-details-status {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }
        .msg-details-status-icon {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }
        .msg-details-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.05rem;
        }
        .msg-details-subtitle {
          color: #64748b;
          font-size: 0.8rem;
        }
        .msg-details-section {
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          padding: 18px;
          margin-bottom: 16px;
        }
        .msg-details-section-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
          margin-bottom: 14px;
        }
        .msg-details-section-header i {
          color: #3b82f6;
          font-size: 1.1rem;
        }
        .msg-details-content {
          background: #f8fafc;
          border-left: 3px solid #3b82f6;
          padding: 14px 16px;
          border-radius: 8px;
          font-size: 0.875rem;
          color: #334155;
          line-height: 1.6;
          word-break: break-word;
        }
        .msg-details-content-meta {
          display: flex;
          gap: 20px;
          margin-top: 10px;
          padding: 0 4px;
          font-size: 0.75rem;
          color: #94a3b8;
        }
        .msg-details-content-meta span {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .msg-details-info {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          background: white;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
        }
        .msg-details-info-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .msg-details-info-icon-green { background: #d1fae5; color: #10b981; }
        .msg-details-info-icon-blue  { background: #eff6ff; color: #3b82f6; }
        .msg-details-info-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 2px;
        }
        .msg-details-info-value {
          font-weight: 600;
          color: #0f172a;
          font-size: 0.875rem;
        }
        .msg-details-tech-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #f1f5f9;
          font-size: 0.8rem;
        }
        .msg-details-tech-icon {
          color: #94a3b8;
          font-size: 0.95rem;
        }
        .msg-details-tech-label {
          color: #64748b;
          font-weight: 500;
        }
        .msg-details-tech-value {
          font-weight: 700;
          color: #0f172a;
        }

        /* ========== ANIMATIONS ========== */
        .spinning {
          animation: spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .msg-header {
            padding: 20px;
          }
          .msg-header-title {
            font-size: 1.2rem;
          }
          .msg-stat-value {
            font-size: 1.25rem;
          }
          .msg-pagination {
            flex-direction: column;
          }
          .msg-modal-body {
            padding: 16px;
          }
        }
      `}</style>

      <Footer />
    </div>
  );
}